<template>
  <section v-if="hero" class="sp" :key="tournamentId + ':' + hero.i">
    <div class="sp-stage">
      <div class="sp-map" :style="{ backgroundImage: hero.mapNum != null ? `url(${mapImg(hero.mapNum)})` : 'none' }"></div>
      <div class="sp-shade"></div>
      <div class="sp-top">
        <span class="lx-kicker">{{ kicker }}</span>
        <span class="lx-title sp-title">{{ hero.title }}</span>
        <span class="sp-mapname">{{ hero.mapName }}</span>
      </div>
      <button v-if="prevI != null" class="sp-nav prev" @click="browse(prevI)" :aria-label="'Show ' + matchTitle(prevI)">‹</button>
      <button v-if="nextI != null" class="sp-nav next" @click="browse(nextI)" :aria-label="'Show ' + matchTitle(nextI)">›</button>
      <button v-if="liveI != null && hero.i !== liveI" class="sp-back lx-btn-ghost" @click="manual = null; $emit('back-live')">Back to the live fight</button>
      <div class="sp-field">
        <div v-for="(side, s) in [hero.l, hero.r]" :key="s" class="sp-side" :class="[s ? 'right' : 'left', { lost: hero.winner && hero.winner !== side.key }]">
          <div class="sp-team" :style="{ background: side.bright }"><button class="sp-teamname" @click="$emit('team', side.key)" v-tip="() => teamTipFor(side)">{{ side.label }}</button><b v-if="hero.winner === side.key"> ✓</b><span class="lx-grade sp-tgrade" :style="{ background: teamG(side).color }" v-tip="() => teamTipFor(side)">{{ teamG(side).grade }}</span><ChampStreakBadge v-if="side.key === 'champion' && champStreak" class="sp-streak" :streak="champStreak" /></div>
          <div class="sp-formation">
            <button v-for="(u, k) in side.units" :key="k" class="sp-unit" :class="['slot' + k, { sel: isPicked(s, k) }]" @click="pick(s, k)" v-tip="() => unitTipFor(u, side, s ? hero.l : hero.r)">
              <UnitSprite :unit="u" :team-color="side.pal" :fallback-src="portrait(u, side.label)" :animated="true" :attack-bursts="!hero.winner" :flip="!s" :size="76" />
              <span class="sp-tag">{{ u.Name }} <i class="lx-grade" :style="{ background: unitG(u).color }">{{ unitG(u).grade }}</i></span>
            </button>
          </div>
        </div>
        <div class="sp-vs">VS</div>
      </div>
      <div class="sp-odds"><HeroOdds :o="odds[oddsKey(hero)]" :hero="hero" /></div>
    </div>
    <TurnStrip :l="hero.l" :r="hero.r" :gd="gameData" />
    <div class="sp-units">
      <div v-for="(side, s) in [hero.l, hero.r]" :key="s" class="sp-col">
        <button v-for="(u, k) in side.units" :key="k" class="sp-card" :class="{ sel: isPicked(s, k) }" :style="{ '--tc': side.bright }" @click="pick(s, k)" v-tip="() => unitTipFor(u, side, s ? hero.l : hero.r)">
          <UnitSprite :unit="u" :team-color="side.pal" :fallback-src="portrait(u, side.label)" :flip="!s" :size="40" />
          <span class="lx-grade sp-card-grade" :style="{ background: unitG(u).color }">{{ unitG(u).grade }}</span>
          <EvadeGrid class="sp-card-ev" :ev="evOf(u)" />
          <div class="sp-card-main">
            <div class="sp-card-name">{{ u.Name }} <span>{{ u.Class }} · {{ u.Sign }}</span></div>
            <div class="sp-card-stats"><b>HP</b> {{ stat(u, 'hp') }} <b>Spd</b> {{ stat(u, 'speed') }} <b>Br</b> {{ u.Brave }} <b>Fa</b> {{ u.Faith }}</div>
            <div class="sp-card-kit">{{ [u.Mainhand, u.ActionSkill, u.SupportSkill, u.ReactionSkill].filter(Boolean).join(' · ') }}</div>
          </div>
        </button>
      </div>
    </div>
    <UnitVsPanel v-if="picked" :key="picked.key" :unit="picked.unit" :side="picked.side" :foe="picked.foe" :gd="gameData" :fallback="portrait(picked.unit, picked.side.label)"
                 @close="pickedAt = null" @full="card = { unit: picked.unit, side: picked.side, foe: picked.foe }" />
    <p v-else class="sp-hint">Click a unit to see what it does to the other team.</p>
    <!-- the Battle lab: simulate this match on its map, watch a battle, see the map in 3D (was the Fight View) -->
    <button ref="labBar" class="sp-labbar" :class="{ open: labOpen }" :aria-expanded="String(labOpen)" @click="labOpen = !labOpen">
      <span class="lx-kicker">⚔ Battle lab</span>
      <span class="sp-labbar-sub">Simulate this fight, watch one play out, and see the map in 3D</span>
      <span class="sp-labbar-caret">{{ labOpen ? '▴' : '▾' }}</span>
    </button>
    <BattleLab v-if="labOpen" :hero="hero" :game-data="gameData" />
    <UnitCardNew v-if="card" :unit="card.unit" :side="card.side" :foe="card.foe" :gd="gameData" :fallback="portrait(card.unit, card.side.label)"
                 @close="card = null" />
  </section>
</template>

<script>
// The homepage's spotlight (2026-10 refresh): the fight that's on now, big, on its map's art: both teams in formation
// with their grades, the worded odds (300 sim battles on the worker pool, through the calibrated fit), and a card per
// unit with its grade and evasion. Click a unit for what it does to the other team (UnitVsPanel) and its full card
// (UnitCardNew), the estimated turn order (TurnStrip), and the ⚔ Battle lab drawer (BattleLab: simulate, watch a battle,
// the map in 3D; it replaced the old Fight View). Follows the live match as results come in; ‹ › browse the other matches (a browsed match stays until
// "Back to the live fight" or the next live match). Takes the bracket page's own data (Luxstadio's teams, winners,
// maps, currentMatchIndex).
import UnitSprite from '../UnitSprite.vue';
import UnitVsPanel from './UnitVsPanel.vue';
import UnitCardNew from './UnitCardNew.vue';
import ChampStreakBadge from '../ChampStreakBadge.vue';
import EvadeGrid from './EvadeGrid.vue';
import HeroOdds from './HeroOdds.vue';
import TurnStrip from './TurnStrip.vue';
import { defineAsyncComponent } from 'vue';
import { matchOdds } from './matchOdds.js';
import { unitInfo } from './unitInfo.js';
import { matchColors } from '../../lib/spriteForClass.js';
import { teamBright } from '../../lib/teamColors.js';
import { UNITS_BASE, MAPS_BASE } from '../../lib/assetUrls.js';
import { unitGrade, teamGrade } from '../../lib/unitGrade.js';
import { rosterStats } from '../../lib/preload.js';
import { unitTip, teamTip } from '../../lib/unitTip.js';

const ORDER = ['red', 'blue', 'green', 'yellow', 'white', 'black', 'purple', 'brown'];
const TITLES = ['Quarterfinal 1', 'Quarterfinal 2', 'Quarterfinal 3', 'Quarterfinal 4', 'Semifinal 1', 'Semifinal 2', 'Final', 'Champion match'];
const MAP_RE = /^\s*(\d+)\)\s*(.*)$/;

export default {
  name: 'FightSpotlight',
  components: { UnitSprite, UnitVsPanel, UnitCardNew, EvadeGrid, HeroOdds, ChampStreakBadge, TurnStrip, BattleLab: defineAsyncComponent(() => import('./BattleLab.vue')) },
  props: {
    teams: { type: Array, required: true },            // [{ name, label, roster, palettes }], bracket order + the champion
    winners: { type: Array, default: () => [] },        // the 8 match results (team names), '' while undecided
    maps: { type: Array, default: () => [] },           // "NN) Name" per match
    currentMatchIndex: { type: Number, default: -1 },   // the live match, -1 when the tournament's done
    tournamentId: { type: [String, Number], default: '' },
    gameData: { type: Object, required: true },         // { charStats, itemschanged, notAbles, abilitiesInfo, monsterSkills }
    featured: { type: Number, default: null },          // a match picked from outside (the bracket), else the live one
    champStreak: { type: Number, default: 0 },          // the reigning champion's title defenses, for its badge
    labRequest: { type: Number, default: 0 },           // bump it to open the Battle lab and scroll to it (the bracket's ⚔)
  },
  emits: ['back-live', 'team'],
  data() { return { manual: null, pickedAt: null, card: null, odds: {}, labOpen: false }; },
  computed: {
    byName() { const m = {}; for (const t of this.teams) m[t.name] = t; return m; },
    // the 8 matches with both teams known
    matches() {
      const W = this.winners || [], out = [];
      const pair = (i) => (i < 4 ? [ORDER[2 * i], ORDER[2 * i + 1]] : i === 4 ? [W[0], W[1]] : i === 5 ? [W[2], W[3]] : i === 6 ? [W[4], W[5]] : [W[6], 'champion']);
      for (let i = 0; i < 8; i++) {
        const [a, b] = pair(i), ta = a && this.byName[a], tb = b && this.byName[b];
        if (!ta || !tb || !(ta.roster || []).length || !(tb.roster || []).length) continue;
        const side = (t) => ({ key: t.name, label: t.label, units: t.roster, palettes: t.palettes, bright: teamBright(t.label) });
        const l = side(ta), r = side(tb);
        [l.pal, r.pal] = matchColors(ta, tb);
        const mm = MAP_RE.exec(this.maps[i] || '');
        out.push({ i, title: TITLES[i], l, r, winner: W[i] || null, map: this.maps[i] || '', mapNum: mm ? +mm[1] : null, mapName: mm ? mm[2] : (this.maps[i] || '') });
      }
      return out;
    },
    liveI() { return this.matches.some((m) => m.i === this.currentMatchIndex) ? this.currentMatchIndex : null; },
    // shown: a browsed match, else one picked from outside, else the live one, else the last one played
    hero() {
      const ms = this.matches, want = this.manual ?? this.featured ?? this.liveI;
      return ms.find((m) => m.i === want) || ms[ms.length - 1] || null;
    },
    kicker() { const h = this.hero; return !h ? '' : h.i === this.liveI ? 'Now fighting' : h.winner ? 'Result' : 'Up next'; },
    prevI() { const ms = this.matches, k = ms.indexOf(this.hero); return k > 0 ? ms[k - 1].i : null; },
    nextI() { const ms = this.matches, k = ms.indexOf(this.hero); return k >= 0 && k < ms.length - 1 ? ms[k + 1].i : null; },
    picked() {
      const h = this.hero, p = this.pickedAt;
      if (!h || !p || p.i !== h.i) return null;
      const side = p.s ? h.r : h.l, foe = p.s ? h.l : h.r, unit = side.units[p.k];
      return unit ? { unit, side, foe, key: h.i + ':' + p.s + ':' + p.k } : null;
    },
  },
  watch: {
    // a new live match takes the spotlight back from a browsed one
    currentMatchIndex() { this.manual = null; },
    tournamentId() { this.manual = null; this.pickedAt = null; this.odds = {}; this._ug = null; this._tg = null; this._ev = null; },
    featured() { this.manual = null; },
    labRequest: { immediate: true, handler(n) {
      if (!n) return;
      this.manual = null; // the match asked for, even if another one was being browsed
      this.labOpen = true;
      this.$nextTick(() => { const el = this.$refs.labBar; if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); });
    } },
    hero: { immediate: true, handler(m) { if (m && !this.odds[this.oddsKey(m)]) this.runOdds(m); } },
  },
  methods: {
    matchTitle(i) { return TITLES[i]; },
    browse(i) { this.manual = i; this.pickedAt = null; },
    pick(s, k) { this.pickedAt = this.isPicked(s, k) ? null : { i: this.hero.i, s, k }; },
    isPicked(s, k) { const p = this.pickedAt; return !!p && !!this.hero && p.i === this.hero.i && p.s === s && p.k === k; },
    portrait(u, label) {
      if (u.Gender === 'Monster') return UNITS_BASE + u.Class + '.gif';
      const g = (u.Gender || 'Male')[0];
      return label === 'Champion' ? UNITS_BASE + u.Class + g + '.gif' : UNITS_BASE + u.Class + g + '_' + label + '.gif';
    },
    mapImg(n) { return MAPS_BASE + n + '_1.gif'; },
    stat(u, k) { try { return unitInfo(u, this.gameData).stats[k]; } catch (e) { return '–'; } },
    evOf(u) { this._ev = this._ev || new Map(); if (!this._ev.has(u)) { let e; try { e = unitInfo(u, this.gameData).evade; } catch (err) { e = { front: 0, side: 0, back: 0, magic: 0 }; } this._ev.set(u, e); } return this._ev.get(u); },
    unitG(u) { this._ug = this._ug || new Map(); if (!this._ug.has(u)) this._ug.set(u, unitGrade(u, rosterStats, this.gameData)); return this._ug.get(u); },
    unitTipFor(u, side, foe, foot) { return unitTip(u, { team: side.label, color: side.bright, grade: this.unitG(u), gd: this.gameData, foes: foe.units, foeLabel: foe.label, mates: side.units, foot: foot || 'Click to see ' + u.Name + '’s options against ' + foe.label }); },
    teamTipFor(side) { return teamTip(side.label, side.units, { color: side.bright, tg: this.teamG(side), foot: 'Click for the full team card' }); },
    teamG(side) { this._tg = this._tg || new Map(); if (!this._tg.has(side.units)) this._tg.set(side.units, teamGrade(side.units, rosterStats, this.gameData)); return this._tg.get(side.units); },
    oddsKey(m) { return m.i + ':' + m.l.key + ':' + m.r.key; },
    async runOdds(m) {
      const key = this.oddsKey(m), tid = this.tournamentId;
      this.odds = { ...this.odds, [key]: 'running' };
      try {
        const o = await matchOdds(m.l, m.r, m.map, this.gameData);
        if (tid !== this.tournamentId) return; // a new tournament came in meanwhile
        this.odds = { ...this.odds, [key]: o };
      } catch (e) {
        const { [key]: _, ...rest } = this.odds; this.odds = rest;
      }
    },
  },
};
</script>

<style scoped>
.sp-teamname { padding: 0; border: 0; background: none; font: inherit; color: inherit; text-transform: inherit; letter-spacing: inherit; text-shadow: inherit; cursor: pointer; }
.sp-teamname:hover, .sp-teamname:focus-visible { text-decoration: underline; text-underline-offset: 3px; outline: none; }
.sp-streak { margin-left: 8px; vertical-align: 2px; text-shadow: none; }
.sp { border: 1px solid var(--brass-edge); border-radius: 6px; overflow: hidden; background: var(--panel-bottom); box-shadow: 0 8px 26px rgba(0,0,0,.5); color: var(--ink); margin: 10px 0 18px; text-align: left; }
.sp-stage { position: relative; height: 340px; }
.sp-map { position: absolute; inset: 0; background-size: cover; background-position: center; image-rendering: pixelated; filter: saturate(.85); }
.sp-shade { position: absolute; inset: 0; background: radial-gradient(ellipse at 50% 60%, rgba(8,10,18,.1), rgba(8,10,18,.75) 80%), linear-gradient(180deg, rgba(8,10,18,.75), transparent 30%, transparent 70%, rgba(8,10,18,.85)); }
.sp-top { position: absolute; top: 14px; left: 0; right: 0; display: flex; flex-direction: column; align-items: center; gap: 2px; pointer-events: none; }
.sp-title { font-size: 26px; }
.sp-mapname { font-size: 13px; color: #e6dcc3; font-style: italic; }
.sp-nav { position: absolute; top: 50%; transform: translateY(-50%); z-index: 2; width: 34px; height: 56px; border: 1px solid var(--brass-edge); border-radius: 4px; background: rgba(8,10,18,.6); color: var(--brass-hi); font-size: 30px; line-height: 1; cursor: pointer; }
.sp-nav:hover { background: rgba(8,10,18,.85); }
.sp-nav.prev { left: 8px; } .sp-nav.next { right: 8px; }
.sp-back { position: absolute; top: 14px; right: 14px; z-index: 2; background: rgba(8,10,18,.7); }
.sp-field { position: absolute; left: 6%; right: 6%; top: 92px; bottom: 58px; }
.sp-side { position: absolute; top: 0; bottom: 0; width: 42%; display: flex; flex-direction: column; transition: opacity .3s; }
.sp-side.left { left: 0; align-items: flex-start; } .sp-side.right { right: 0; align-items: flex-end; }
.sp-side.lost { opacity: .45; filter: grayscale(.7); }
.sp-team { padding: 2px 14px; font-family: var(--cond); font-weight: 700; font-size: 18px; letter-spacing: 1.2px; text-transform: uppercase; color: #fff; text-shadow: 0 1px 0 rgba(0,0,0,.6); }
.sp-side.left .sp-team { clip-path: polygon(0 0, 100% 0, calc(100% - 10px) 100%, 0 100%); padding-right: 22px; }
.sp-side.right .sp-team { clip-path: polygon(10px 0, 100% 0, 100% 100%, 0 100%); padding-left: 22px; }
.sp-tgrade { margin-left: 10px; font-size: 14px; }
.sp-formation { position: relative; flex: 1; width: 100%; }
.sp-unit { position: absolute; display: flex; flex-direction: column; align-items: center; background: none; border: 0; padding: 0; cursor: pointer; color: inherit; font: inherit; filter: drop-shadow(0 6px 3px rgba(0,0,0,.6)); }
.sp-unit:hover { filter: drop-shadow(0 0 6px rgba(243,214,142,.6)) drop-shadow(0 6px 3px rgba(0,0,0,.6)); }
.sp-unit.sel { filter: drop-shadow(0 0 8px var(--brass-hi)) drop-shadow(0 0 2px var(--brass-hi)); }
.sp-side.left .slot0 { left: 52%; top: 4%; } .sp-side.left .slot1 { left: 14%; top: 0; } .sp-side.left .slot2 { left: 62%; top: 44%; } .sp-side.left .slot3 { left: 24%; top: 40%; }
.sp-side.right .slot0 { right: 52%; top: 4%; } .sp-side.right .slot1 { right: 14%; top: 0; } .sp-side.right .slot2 { right: 62%; top: 44%; } .sp-side.right .slot3 { right: 24%; top: 40%; }
.sp-tag { margin-top: -2px; padding: 0 6px; border-radius: 3px; background: rgba(8,10,18,.78); font-family: var(--cond); font-weight: 600; font-size: 13px; color: var(--ink-hi); white-space: nowrap; }
.sp-tag i { margin-left: 4px; font-size: 12px; padding: 0 4px; }
.sp-vs { position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); width: 54px; height: 54px; border-radius: 50%; display: grid; place-items: center;
  font-family: var(--display); font-size: 20px; color: #1a1408; background: linear-gradient(#f3d68e, #a27a35); box-shadow: 0 3px 10px rgba(0,0,0,.7); }
.sp-odds { position: absolute; left: 0; right: 0; bottom: 10px; }
.sp-units { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 1px; background: var(--line); border-top: 1px solid var(--brass-edge); }
@media (max-width: 760px) { .sp-units { grid-template-columns: minmax(0, 1fr); } .sp-stage { height: 300px; } }
.sp-col { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 1px; }
@media (max-width: 1100px) { .sp-col { grid-template-columns: minmax(0, 1fr); } }
.sp-card { position: relative; display: flex; gap: 8px; align-items: center; padding: 8px 10px; background: var(--navy); border: 0; border-left: 3px solid var(--tc); font: inherit; color: inherit; text-align: left; cursor: pointer; }
.sp-card:hover { background: #18203a; }
.sp-card.sel { background: #222b45; box-shadow: inset 0 0 0 1px var(--brass); }
.sp-card-grade { position: absolute; left: 8px; bottom: 6px; font-size: 12px; padding: 0 4px; }
.sp-card-ev { margin-left: auto; order: 3; flex: none; }
.sp-card-main { min-width: 0; }
.sp-card-name { font-family: var(--cond); font-weight: 700; font-size: 15px; color: var(--ink-hi); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sp-card-name span { font-weight: 600; font-size: 12.5px; color: var(--mute); margin-left: 4px; }
.sp-card-stats { font-size: 12px; }
.sp-card-stats b { font-family: var(--cond); font-weight: 600; color: var(--brass); margin-left: 6px; }
.sp-card-stats b:first-child { margin-left: 0; }
.sp-card-kit { font-size: 11.5px; color: var(--mute); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sp-labbar { display: flex; align-items: baseline; gap: 12px; width: 100%; padding: 9px 14px; border: 0; border-top: 1px solid var(--brass-edge); background: var(--navy2); color: var(--ink); font: inherit; text-align: left; cursor: pointer; scroll-margin-top: 60px; }
.sp-labbar:hover, .sp-labbar.open { background: #151b2a; }
.sp-labbar-sub { font-size: 13px; color: var(--mute); }
.sp-labbar-caret { margin-left: auto; color: var(--brass); }
@media (max-width: 560px) { .sp-labbar-sub { display: none; } }
.sp-hint { margin: 0; padding: 8px 14px; font-size: 12.5px; color: var(--mute); font-style: italic; background: var(--panel-bottom); }
</style>
